import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FACEBOOK_REDIRECT_URI = 'https://wantd.au/auth/facebook/callback';

export default function FacebookCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (error) {
        setStatus('error');
        setErrorMessage(errorDescription || 'Facebook login was cancelled or failed');
        return;
      }

      if (!code) {
        setStatus('error');
        setErrorMessage('No authorization code received from Facebook');
        return;
      }

      try {
        // Exchange code for token via backend
        const response = await fetch(`${API_URL}/api/auth/facebook/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: code,
            redirect_uri: FACEBOOK_REDIRECT_URI
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to authenticate with Facebook');
        }

        const data = await response.json();
        
        // Login with the received token
        await loginWithToken(data.access_token, data.user);
        
        setStatus('success');
        
        // Show appropriate toast message
        if (data.is_new_user) {
          toast.success('Welcome to WANTD! Your account has been created.');
        } else {
          toast.success('Successfully signed in with Facebook!');
        }

        // Redirect after a short delay
        setTimeout(() => {
          // Check if admin
          if (data.user.role === 'admin' || data.user.role === 'superadmin') {
            navigate('/admin', { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        }, 1500);

      } catch (err) {
        console.error('Facebook callback error:', err);
        setStatus('error');
        setErrorMessage(err.message || 'Authentication failed. Please try again.');
      }
    };

    handleCallback();
  }, [searchParams, loginWithToken, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--theme-bg)' }}>
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <img 
          src="/logo-wantd-navy.png"
          alt="WANTD" 
          className="h-12 w-auto mx-auto mb-8"
        />

        {/* Card */}
        <div className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-2xl p-8 shadow-card">
          {status === 'processing' && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-[#1877F2] mx-auto mb-4" />
              <h1 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">
                Signing in with Facebook
              </h1>
              <p className="text-[var(--theme-text-secondary)]">
                Please wait while we verify your account...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">
                Success!
              </h1>
              <p className="text-[var(--theme-text-secondary)]">
                Redirecting you to WANTD...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">
                Authentication Failed
              </h1>
              <p className="text-[var(--theme-text-secondary)] mb-6">
                {errorMessage}
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12"
                >
                  Back to Login
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.location.reload()}
                  className="w-full border-[var(--theme-border)] text-[var(--theme-text-primary)] rounded-full h-12"
                >
                  Try Again
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
